import React, { useMemo } from "react";
import { ConsolidationGroupStatus } from "@common/types/domains/accounting/consolidation";
import { Entity } from "@common/types/entity";
import { ConsolidatedAvatar } from "@components/avatar/ConsolidatedAvatar";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { JournalEntryConsolidationContext } from "./journalEntryConsolidationContext";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { HStack } from "@design-system/HStack/HStack";
import {
    isJournalEntryScopeEqual,
    JournalEntryScope,
} from "./journalEntryScope";

type JournalEntryScopeOption =
    | (Extract<JournalEntryScope, { kind: "entity" }> & {
          label: string;
          entity: Entity;
      })
    | (Extract<JournalEntryScope, { kind: "consolidationGroup" }> & {
          label: string;
      });

interface JournalEntryScopeSelectProps {
    entities: readonly Entity[];
    consolidationContext?: JournalEntryConsolidationContext;
    value: JournalEntryScope;
    onValueChange: (scope: JournalEntryScope) => void;
    disabled?: boolean;
}

function renderScopeIdentity(option: JournalEntryScopeOption): React.ReactNode {
    switch (option.kind) {
        case "entity":
            return <EntityDisplay entity={option.entity} />;
        case "consolidationGroup":
            return (
                <HStack gap={8} align="center">
                    <ConsolidatedAvatar
                        name={option.label}
                        size="xs"
                        aria-hidden
                    />
                    <span>{option.label}</span>
                </HStack>
            );
        default: {
            const exhaustiveScope: never = option;
            return exhaustiveScope;
        }
    }
}

function renderScopeIcon(option: JournalEntryScopeOption): React.ReactNode {
    switch (option.kind) {
        case "entity":
            return (
                <EntityAvatar entity={option.entity} size="xs" aria-hidden />
            );
        case "consolidationGroup":
            return (
                <ConsolidatedAvatar name={option.label} size="xs" aria-hidden />
            );
        default: {
            const exhaustiveScope: never = option;
            return exhaustiveScope;
        }
    }
}

function scopeOptionKey(option: JournalEntryScopeOption): string {
    return option.kind === "entity"
        ? `entity:${option.entityId}`
        : `consolidation-group:${option.consolidationGroupId}`;
}

function optionToScope(option: JournalEntryScopeOption): JournalEntryScope {
    switch (option.kind) {
        case "entity":
            return { kind: option.kind, entityId: option.entityId };
        case "consolidationGroup":
            return {
                kind: option.kind,
                consolidationGroupId: option.consolidationGroupId,
            };
        default: {
            const exhaustiveScope: never = option;
            return exhaustiveScope;
        }
    }
}

export const JournalEntryScopeSelect: React.FC<
    JournalEntryScopeSelectProps
> = ({
    entities,
    consolidationContext,
    value,
    onValueChange,
    disabled = false,
}) => {
    const options = useMemo<JournalEntryScopeOption[]>(() => {
        const entityOptions: JournalEntryScopeOption[] = entities
            .filter((entity) => entity.isBusiness)
            .map((entity) => ({
                kind: "entity",
                entityId: entity.id,
                label: entity.name ?? "Unnamed entity",
                entity,
            }));

        if (
            !consolidationContext ||
            consolidationContext.group.status !==
                ConsolidationGroupStatus.active
        ) {
            return entityOptions;
        }

        return [
            {
                kind: "consolidationGroup",
                consolidationGroupId: consolidationContext.group.id,
                label: consolidationContext.displayName,
            },
            ...entityOptions,
        ];
    }, [consolidationContext, entities]);

    const optionsByKey = useMemo(
        () =>
            new Map(options.map((option) => [scopeOptionKey(option), option])),
        [options],
    );
    const items = useMemo<Array<PickerItemData<string>>>(
        () =>
            options.map((option) => ({
                value: scopeOptionKey(option),
                label: option.label,
                leading: renderScopeIcon(option),
            })),
        [options],
    );

    const selectedOption =
        options.find((option) => isJournalEntryScopeEqual(option, value)) ??
        null;

    return (
        <Picker.Select
            items={items}
            searchable
            searchPlaceholder="Search entities..."
            emptyLabel="No matching entities"
            value={selectedOption ? scopeOptionKey(selectedOption) : null}
            onValueChange={(key) => {
                const option = key === null ? undefined : optionsByKey.get(key);
                if (option) {
                    onValueChange(optionToScope(option));
                }
            }}
            disabled={disabled}
            placeholder="Select entity"
            renderValue={() =>
                selectedOption ? renderScopeIdentity(selectedOption) : undefined
            }
        />
    );
};
